import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { useState } from "react"
import { useApp } from "../../AppProvider"
import { useConditions, useDeleteCondition, type Condition } from "../../hooks/useConditions"
import { classNames } from "../../utils/classNames"
import { Button } from "../common"
import { ErrorMessage } from "../common/Feedback/ErrorMessage"
import { LoadingMessage } from "../common/Feedback/LoadingMessage"
import Modal from "../common/Modal"
import { ConfirmModal } from "../ConfirmModal"
import { ConditionForm } from "./ConditionForm"

const columnHelper = createColumnHelper<Condition>()
const columns = [
  columnHelper.accessor("name", {
    header: "Condition",
    cell: (info) => <span className="font-semibold text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor("recommendations", {
    header: "Messages",
    cell: (info) => {
      const recommendations = info.getValue()
      return (
        <div className="max-w-md">
          {recommendations.length === 0 ? (
            <span className="text-gray-400 italic">No messages</span>
          ) : (
            <div className="space-y-1">
              {recommendations.slice(0, 2).map((rec, index) => (
                <div key={index} className="text-sm text-gray-600">
                  <div className="line-clamp-1">{rec.message}</div>
                </div>
              ))}
              {recommendations.length > 2 && (
                <div className="text-xs text-gray-400">+{recommendations.length - 2} more...</div>
              )}
            </div>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor("createdAt", {
    header: "Created",
    cell: (info) => (
      <span className="text-sm text-gray-500">{new Date(info.getValue()).toLocaleDateString()}</span>
    ),
  }),
  columnHelper.display({
    id: "actions",
    header: (props: any) => (
      <div className="flex justify-end">
        <Button onClick={() => props.setFormState({ isOpen: true })}>Add condition</Button>
      </div>
    ),
    cell: (props: any) => {
      return (
        <div className="flex items-center justify-end gap-x-2">
          <Button
            variant="outline"
            onClick={() => props.setFormState({ isOpen: true, condition: props.row.original })}>
            Edit
          </Button>
          <Button variant="danger" onClick={() => props.setConditionToDelete(props.row.original)}>
            Delete
          </Button>
        </div>
      )
    },
  }),
]

interface Props {
  organizationId: string
}

export const ConditionsTable = ({ organizationId }: Props) => {
  const { token } = useApp()
  const conditionsQuery = useConditions(organizationId, token)
  const deleteConditionMutation = useDeleteCondition(organizationId, token)

  const [formState, setFormState] = useState<{
    isOpen: boolean
    condition?: Condition
  }>({
    isOpen: false,
  })

  const [conditionToDelete, setConditionToDelete] = useState<Condition | undefined>(undefined)

  const data = conditionsQuery.data ?? []

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleDeleteCondition = async () => {
    if (!conditionToDelete) return

    try {
      await deleteConditionMutation.mutateAsync(conditionToDelete._id)
      setConditionToDelete(undefined)
    } catch (error) {
      console.error("Failed to delete condition:", error)
    }
  }

  return (
    <div>
      {conditionsQuery.isLoading ? (
        <LoadingMessage />
      ) : conditionsQuery.isError ? (
        <ErrorMessage message={conditionsQuery?.error?.message ?? "Failed to load conditions"} />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-white">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, i) => (
                    <th
                      scope="col"
                      className={classNames(
                        "py-3.5 text-left text-sm font-semibold text-gray-900",
                        i === 0
                          ? "pr-3 pl-4 sm:pl-6"
                          : i === headerGroup.headers.length - 1
                            ? "pr-4 pl-3 sm:pr-6"
                            : "px-3",
                      )}
                      key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, {
                            ...header.getContext(),
                            setFormState,
                          })}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center font-medium text-gray-600">
                    No conditions found
                  </td>
                </tr>
              )}
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, i) => (
                    <td
                      className={classNames(
                        "py-4 text-sm font-medium text-gray-900",
                        i === 0
                          ? "pr-3 pl-4 sm:pl-6"
                          : i === row.getVisibleCells().length - 1
                            ? "pr-4 pl-3 sm:pr-6"
                            : "px-3",
                      )}
                      key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, {
                        ...cell.getContext(),
                        setFormState,
                        setConditionToDelete,
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={formState.isOpen}
        setOpen={(val) => setFormState({ ...formState, isOpen: val as boolean })}>
        <ConditionForm
          organizationId={organizationId}
          condition={formState.condition}
          onSuccess={() => setFormState({ isOpen: false })}
        />
      </Modal>

      <ConfirmModal
        open={!!conditionToDelete}
        setOpen={() => setConditionToDelete(undefined)}
        onCancel={() => setConditionToDelete(undefined)}
        onConfirm={handleDeleteCondition}
        confirmVariant="danger"
        header="Delete condition"
        body={
          <>
            Are you sure you want to delete the condition <strong>{conditionToDelete?.name}</strong>? This
            action cannot be undone.
          </>
        }
      />
    </div>
  )
}
